import { Mixin } from 'vue-mixin-decorator'
import MomentMixin from '@/_mixins/moment'
import { languages } from '@/types/languages'
import { parsePhoneNumber, CountryCode, NumberFormat } from 'libphonenumber-js'
import { deserialise } from 'kitsu-core'
import { cloneDeep } from '@apollo/client/utilities'

@Mixin
export default class AppMixin extends MomentMixin {
    avalableModules = [
        'contractor',
        'observation',
        'hse',
        'psi',
        'personnel',
        'document',
        'course',
        'access',
    ]

    formatLangTo6391(lang: string) {
        let langRes = languages.find((langObj) => langObj['639-2'] == lang)
        return langRes ? langRes['639-1'].split('-')[0] : 'en'
    }

    formatPhoneNumber(phone: string, countryCode?: CountryCode, format?: NumberFormat) {
        if (!phone) {
            return ''
        }

        let formattedPhoneNumber = phone

        try {
            const phoneNumber = parsePhoneNumber(phone, countryCode)
            formattedPhoneNumber = phoneNumber.format(format || 'INTERNATIONAL')
        } catch {
            // ignore parsing error (for example TOO_SHORT)
        }
        return formattedPhoneNumber
    }

    isStringPhone(str) {
        return new RegExp(/^\+|^[0-9-()\s]*$/).test(str)
    }

    phoneHasCode(str) {
        return new RegExp(/^\+\d[0-9-()\s]+$|/).test(str)
    }

    sanitizeUsername(phone) {
        return this.isStringPhone(phone)
            ? phone
                  .replace(/\s/g, '')
                  .replace(/-/g, '')
                  .replace(/\(/g, '')
                  .replace(/\)/g, '')
                  .trim()
            : phone.replace(/\s/g, '').trim()
    }

    generateUUID() {
        return crypto['randomUUID']()
    }

    get versionText(): string {
        return process.env.VUE_APP_VERSION
    }

    get languagesAvailableForUsers() {
        return Object.values(this.$store.getters.languages).filter((language: any) => {
            return language?.availableForUsers
        })
    }

    setSystemLanguage() {
        let lang = 'eng'

        let systemLanguages = window.navigator.languages.map((systemlanguage) => {
            let langRes = languages.find((langObj) => langObj['639-1'] == systemlanguage)
            let lang2 = langRes ? langRes['639-2'] : 'eng'
            return lang2
        })

        systemLanguages = systemLanguages.filter((systemlanguage) => {
            return this.languagesAvailableForUsers.includes(systemlanguage)
        })

        this.$store.commit('systemLanguages', systemLanguages)

        return lang
    }

    // Authentication vars
    get isAuthenticated() {
        return this.$store.getters.authenticated
    }

    get currentProjectUser(): any {
        return this.$store.getters.projectUser
    }

    // User vars
    get currentUserDeserialised() {
        return deserialise({ data: cloneDeep(this.$store.getters.user) }).data
    }

    get currentUser() {
        return this.$store.getters.user
    }

    get isSuperuser() {
        return this.currentUser ? this.currentUser.attributes.isSuperuser : false
    }

    get isImpersonating() {
        return this.$store.getters.impersonating ? true : false
    }

    get impersonated() {
        return this.$store.getters.impersonating
    }

    get userLanguage() {
        return this.$store.getters.userLanguage
    }

    get employer() {
        return this.$store.getters.userCompany
    }

    // Route
    get view() {
        return this.$route?.matched[0]?.name
    }

    get routerId() {
        return this.$route.params.id
    }

    get requireProject() {
        return (
            this.$route.matched.findIndex((item) => (item.meta['requireProject'] ? true : false)) >
            -1
        )
    }

    // Project
    get isProject() {
        return this.$route.params.type == 'p' ? true : false
    }

    get isGroup() {
        return this.$route.params.type == 'g' ? true : false
    }

    // Current project can be set to either project id on project view or to one of the projects in group view
    get currentProjectId(): string | null {
        return this.isProject ? this.$route.params.id : this.$store.getters.currentProjectId
    }

    set currentProjectId(id: string | null) {
        if (this.requireProject) {
            if (id && id != this.$route.params.projectId) {
                this.$router.replace({ params: { projectId: id } })
            }
            this.$store.commit('currentProjectId', id)
        }
    }

    get currentProject() {
        return this.currentProjectId ? this.$store.getters.project(this.currentProjectId) : null
    }

    get currentProjectName(): string {
        return this.currentProject ? this.currentProject.attributes.name : ''
    }

    get currentProjectPermissions(): Array<string> {
        let contractorPermissions =
            this.currentProject?.attributes.contractorPermissions[
                this.currentProjectUser?.displayContractor?._id
            ] || []
        return this.currentProject
            ? [
                  ...new Set([
                      ...this.currentProject.attributes.permissions,
                      ...contractorPermissions,
                  ]),
              ]
            : []
    }

    get currentGroup() {
        return this.isGroup ? this.$store.getters.group(this.$route.params.id) : null
    }

    get allProjects(): any {
        return Object.values(this.$store.getters.projects)
    }

    get allPermissions() {
        const projectPermissions = this.allProjects
            .map((project) => project.attributes.permissions)
            .flat()

        let contractorPermissions =
            this.currentProject?.attributes.contractorPermissions[
                this.currentProjectUser?.displayContractor?._id
            ] || []

        return [...new Set([...projectPermissions, ...contractorPermissions])]
    }

    get activeModules() {
        return Object.values(this.$store.getters.projectModules)
    }

    isModuleViewAccessible(modulePermissionIdentifier: string) {
        let permissions = this.currentProject
            ? this.$store.getters.projectPermissions(
                  this.currentProject.id,
                  this.currentProjectUser?.displayContractor?._id
              )
            : this.$store.getters.projectsGroupPermissions(this.currentGroup.id)

        return this.hasPermission(permissions, modulePermissionIdentifier)
    }

    isModulePresent(name: string) {
        return (
            this.activeModules.findIndex((module: any) => {
                return module.relationships.module.data.id == name
            }) > -1
        )
    }

    isModuleAccessible(name: string) {
        return (
            this.activeModules.findIndex((module: any) => {
                return (
                    module?.attributes.isAccessible && module.relationships.module.data.id == name
                )
            }) > -1
        )
    }

    isModuleActive(moduleId: string) {
        return (
            this.activeModules.findIndex(
                (module: any) =>
                    module.relationships.module.data.id == moduleId &&
                    module.attributes.status == 'active'
            ) > -1
        )
    }

    isModuleArchived(moduleId: string) {
        return (
            this.activeModules.findIndex(
                (module: any) =>
                    module.relationships.module.data.id == moduleId &&
                    module.attributes.status == 'archived'
            ) > -1
        )
    }

    isModuleClosed(moduleId: string) {
        return (
            this.activeModules.findIndex(
                (module: any) =>
                    module.relationships.module.data.id == moduleId &&
                    module.attributes.status == 'closed'
            ) > -1
        )
    }

    isModuleDeleted(moduleId: string) {
        return (
            this.activeModules.findIndex(
                (module: any) =>
                    module.relationships.module.data.id == moduleId &&
                    module.attributes.status == 'deleted'
            ) > -1
        )
    }

    isModuleDeletionAt(moduleId: string) {
        let module: any = this.activeModules.find((module: any) => {
            if (module.relationships.module.data.id == moduleId && !!module.attributes.deletionAt) {
                return module
            } else {
                return null
            }
        })
        return module ? module.attributes.deletionAt : null
    }

    // Authorization
    hasPermission(availablePermissions, requiredPermission) {
        if (requiredPermission == undefined) {
            return true
        } else if (typeof requiredPermission == 'string') {
            return this.isSuperuser
                ? true
                : availablePermissions.findIndex((item: any) => {
                      return item.includes(requiredPermission)
                  }) > -1
                ? true
                : false
        } else {
            return this.isSuperuser
                ? true
                : availablePermissions.length == 0
                ? false
                : requiredPermission.length > 0
                ? requiredPermission.findIndex((permission) =>
                      availablePermissions.includes(permission)
                  ) > -1
                : true
        }
    }

    hasAnyPermissions(permissions) {
        return this.isSuperuser ? true : permissions.length > 0 ? true : false
    }

    get requiredPermissions() {
        return this.$route.matched
            .map((item) => (item.meta['permission'] ? item.meta['permission'] : []))
            .flat()
    }

    // Personnel
    get showTagInterface() {
        return this.isProject ? this.$store.getters.showTagInterface : false
    }

    sanitizeFilename(fileName: string) {
        // replace invalid chracters <>:"/\|?* with _
        return fileName.replace(/[<>:"/\\|?*]/g, '_')
    }

    async downloadImage(imageSrc, name?) {
        const image = await fetch(imageSrc)
        const imageBlog = await image.blob()
        const imageURL = URL.createObjectURL(imageBlog)
        const link = document.createElement('a')

        link.href = imageURL
        if (name) {
            link.download = this.sanitizeFilename(name)
        }
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    resetData() {
        this.$store.dispatch('clearAll')
    }
}
